import { useState, useRef } from 'react'
import { motion, useInView } from 'framer-motion'

const InteractiveSkills = () => {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const [activeCategory, setActiveCategory] = useState('backend')

  const skillCategories = {
    backend: {
      title: 'Backend & Security',
      prefix: 'sys',
      skills: [
        { name: 'Node.js', level: 95 },
        { name: 'Python', level: 88 },
        { name: 'MongoDB', level: 92 },
        { name: 'Security Hardening (JWT, CSRF, Rate Limiting)', level: 95 },
        { name: 'Multi-tenant Architecture', level: 93 },
        { name: 'Fine-grained Access Control (RBAC)', level: 95 }
      ]
    },
    data: {
      title: 'Data & Intelligence',
      prefix: 'data',
      skills: [
        { name: 'Business Intelligence Systems', level: 92 },
        { name: 'Multi-source Data Aggregation', level: 90 },
        { name: 'Automated Executive Reporting', level: 88 },
        { name: 'ETL Pipelines', level: 87 },
        { name: 'Data Migration (Zero-downtime)', level: 95 },
        { name: 'Analytics & Metrics Tracking', level: 85 }
      ]
    },
    ai: {
      title: 'AI & Automation',
      prefix: 'ai',
      skills: [
        { name: 'LLM Integration (OpenAI, Claude)', level: 92 },
        { name: 'AI Agents & Workflows', level: 90 },
        { name: 'MCP Protocol', level: 95 },
        { name: 'Business Process Automation', level: 93 },
        { name: 'Prompt Engineering', level: 90 },
        { name: 'Open Source Tooling', level: 88 }
      ]
    },
    infra: {
      title: 'Infrastructure & Frontend',
      prefix: 'ops',
      skills: [
        { name: 'System Architecture', level: 93 },
        { name: 'Graceful Shutdown & Failure Fallbacks', level: 90 },
        { name: 'Structured Logging & Observability', level: 88 },
        { name: 'React', level: 90 },
        { name: 'API Design & Integration', level: 92 },
        { name: 'Production Deployment', level: 90 }
      ]
    }
  }

  const active = skillCategories[activeCategory]

  return (
    <section ref={sectionRef} className="py-16 sm:py-24" style={{ background: 'var(--bg-surface)' }}>
      <div className="container mx-auto px-4 sm:px-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-10 sm:mb-16"
        >
          <div className="font-mono text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
            <span style={{ color: 'var(--syntax-green)' }}>// </span>
            system status
          </div>
          <h2 className="font-mono text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            Skills & <span style={{ color: 'var(--accent)' }}>Expertise</span>
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }} className="text-lg max-w-2xl">
            I design for what breaks at 2AM, not just what works in demos. Every skill here is battle-tested in production systems serving real users.
          </p>
        </motion.div>

        {/* Category tabs - styled as terminal tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-wrap gap-2 mb-8 sm:mb-12"
        >
          {Object.entries(skillCategories).map(([key, cat]) => (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`font-mono text-[10px] sm:text-xs px-2.5 sm:px-4 py-1.5 sm:py-2 rounded transition-all duration-300 border ${
                activeCategory === key
                  ? 'border-[#00FFE0] text-[#00FFE0] bg-[rgba(0,255,224,0.08)]'
                  : 'border-[#1E1E2E] text-[#8888A0] hover:border-[#2A2A3E] hover:text-[#E0E0E8]'
              }`}
            >
              <span className="sm:hidden">{cat.prefix}</span>
              <span className="hidden sm:inline">[{cat.prefix}] {cat.title}</span>
            </button>
          ))}
        </motion.div>

        {/* Skills grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 max-w-4xl">
          {active.skills.map((skill, index) => (
            <motion.div
              key={`${activeCategory}-${skill.name}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="terminal-card"
            >
              <div className="p-4">
                {/* Skill name and level */}
                <div className="flex justify-between items-center mb-3">
                  <span className="font-mono text-xs sm:text-sm" style={{ color: 'var(--text-primary)' }}>
                    {skill.name}
                  </span>
                  <span className="font-mono text-xs font-bold" style={{ color: 'var(--accent)' }}>
                    {skill.level}%
                  </span>
                </div>

                {/* Progress bar */}
                <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-elevated)' }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${skill.level}%` }}
                    transition={{ duration: 0.8, delay: 0.2 + index * 0.05, ease: 'easeOut' }}
                    className="h-full rounded-full"
                    style={{
                      background: `linear-gradient(90deg, var(--accent), var(--accent-dim))`,
                      boxShadow: '0 0 8px var(--accent-glow)'
                    }}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-10 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-3xl"
        >
          {[
            { value: '14k+', label: 'Users Served', color: 'var(--accent)' },
            { value: '12+', label: 'Data Sources', color: 'var(--syntax-green)' },
            { value: '50+', label: 'Branches', color: 'var(--syntax-purple)' },
            { value: '37', label: 'Open Source Tools', color: 'var(--syntax-amber)' }
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div className="font-mono text-2xl sm:text-3xl font-bold mb-1" style={{ color: stat.color }}>
                {stat.value}
              </div>
              <div className="font-mono text-[10px] sm:text-xs" style={{ color: 'var(--text-muted)' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

      </div>
    </section>
  )
}

export default InteractiveSkills
